/**
 * FHIR CodeSystems, ValueSets, and Extensions URLs
 *
 * This file contains centralized constants for all FHIR system URLs used throughout the application.
 * Organizing these in one place ensures consistency and makes it easy to update URLs if needed.
 *
 * URL Structure:
 * - Standard FHIR: http://hl7.org/fhir/...
 * - HL7 Terminology: http://terminology.hl7.org/...
 * - US Core Extensions: http://hl7.org/fhir/us/core/StructureDefinition/...
 * - Custom (replace alopexcare.com): http://alopexcare.com/...
 */

/**
 * HL7 Standard CodeSystems
 * Official FHIR terminology systems maintained by HL7
 */
export const HL7_CODE_SYSTEMS = {
	/** Identifier types (MR, SS, DL, etc.) */
	IDENTIFIER_TYPE: 'http://terminology.hl7.org/CodeSystem/v2-0203',

	/** Marital status codes (M, S, D, W, etc.) */
	MARITAL_STATUS: 'http://terminology.hl7.org/CodeSystem/v3-MaritalStatus',

	/** Administrative gender (male, female, other, unknown) */
	ADMINISTRATIVE_GENDER: 'http://hl7.org/fhir/administrative-gender',

	/** Contact relationship (emergency, family, etc.) */
	CONTACT_RELATIONSHIP: 'http://terminology.hl7.org/CodeSystem/v2-0131',

	/** Address use (home, work, temp, old) */
	ADDRESS_USE: 'http://hl7.org/fhir/address-use',

	/** Contact point use (home, work, mobile) */
	CONTACT_POINT_USE: 'http://hl7.org/fhir/contact-point-use',
} as const;

/**
 * Standard FHIR Identifier Systems
 * Systems used for patient/practitioner identifiers
 */
export const FHIR_IDENTIFIER_SYSTEMS = {
	/** US Social Security Number */
	US_SSN: 'http://hl7.org/fhir/sid/us-ssn',

	/** US National Provider Identifier (NPI) */
	US_NPI: 'http://hl7.org/fhir/sid/us-npi',

	/** US Driver's License */
	US_DRIVERS_LICENSE: 'http://hl7.org/fhir/sid/us-dl',

	/** ICD-10 diagnosis codes */
	ICD_10: 'http://hl7.org/fhir/sid/icd-10',

	/** ICD-10-CM (Clinical Modification - US specific) */
	ICD_10_CM: 'http://hl7.org/fhir/sid/icd-10-cm',

	/** SNOMED CT clinical terminology */
	SNOMED_CT: 'http://snomed.info/sct',

	/** LOINC (Logical Observation Identifiers Names and Codes) */
	LOINC: 'http://loinc.org',

	/** RxNorm (medication coding) */
	RXNORM: 'http://www.nlm.nih.gov/research/umls/rxnorm',
} as const;

/**
 * US Core FHIR Extensions
 * Extensions defined in the US Core Implementation Guide
 */
export const US_CORE_EXTENSIONS = {
	/** US Core Race Extension */
	RACE: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-race',

	/** US Core Ethnicity Extension */
	ETHNICITY: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity',

	/** US Core Birth Sex Extension */
	BIRTH_SEX: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex',

	/** US Core Tribal Affiliation Extension */
	TRIBAL_AFFILIATION: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-tribal-affiliation',

	/** US Core Individual Pronouns Extension */
	INDIVIDUAL_PRONOUNS:
		'http://hl7.org/fhir/us/core/StructureDefinition/us-core-individual-pronouns',
} as const;

/**
 * Time Entry Extensions
 * Extensions for tracking time entries on Task resources
 */
export const TIME_ENTRY_EXTENSIONS = {
	/** Duration in minutes for time entries */
	DURATION_MINUTES: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-duration',

	/** Time entry category (administrative, clinical, training, etc.) */
	TIME_CATEGORY: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-category',

	/** Whether time is billable */
	IS_BILLABLE: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-billable',

	/** Associated project/activity reference */
	PROJECT_REFERENCE: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-project',

	/** Notes/description for the time entry */
	NOTES: 'http://alopexcare.com/fhir/StructureDefinition/time-entry-notes',
} as const;

/**
 * Custom Organization Extensions
 * Application-specific extensions for data not covered by standard FHIR
 *
 * TODO: Replace 'alopexcare.com' with your actual organization's base URL
 * Example: 'https://fhir.mycompany.com/StructureDefinition/...'
 */
export const CUSTOM_EXTENSIONS = {
	/** Patient's religion */
	RELIGION: 'http://alopexcare.com/religion',

	/** Patient's timezone ID */
	TIMEZONE_ID: 'http://alopexcare.com/timezone-id',

	/** Associated pharmacy ID */
	PHARMACY_ID: 'http://alopexcare.com/pharmacy-id',

	/** Associated pharmacy email */
	PHARMACY_EMAIL: 'http://alopexcare.com/pharmacy-email',

	/** Referral source */
	REFERRAL_SOURCE: 'http://alopexcare.com/referral-source',

	/** Referral date */
	REFERRAL_DATE: 'http://alopexcare.com/referral-date',

	/** Practice name */
	PRACTICE_NAME: 'http://alopexcare.com/practice-name',

	/** Facility name */
	FACILITY: 'http://alopexcare.com/facility',

	/** MCHC (Managed Care Health Center) */
	MCHC: 'http://alopexcare.com/mchc',

	/** User status (active, inactive, locked) */
	USER_STATUS: 'http://alopexcare.com/user-status',

	/** Practitioner professional summary/bio */
	PROFESSIONAL_SUMMARY: 'http://alopexcare.com/professional-summary',

	/** Practitioner work experience (complex extension) */
	WORK_EXPERIENCE: 'http://alopexcare.com/work-experience',

	/** Practitioner professional awards (complex extension) */
	PROFESSIONAL_AWARD: 'http://alopexcare.com/professional-award',

	/** Practitioner professional skills */
	PROFESSIONAL_SKILLS: 'http://alopexcare.com/professional-skills',

	/** Medical group affiliation marker */
	MEDICAL_GROUP_AFFILIATION: 'http://alopexcare.com/medical-group-affiliation',

	/** Legacy entity ID for organization references during migration */
	LEGACY_ENTITY_ID: 'http://alopexcare.com/legacy-entity-id',

	/** Task defer/snooze metadata (deferCount, lastDeferReason, lastDeferredAt) */
	TASK_DEFER_METADATA: 'http://alopexcare.com/fhir/StructureDefinition/task-defer-metadata',

	/** Task urgency level (0-10 scale, separate from FHIR priority) */
	TASK_URGENCY: 'http://alopexcare.com/fhir/StructureDefinition/task-urgency',

	/** Task estimated duration */
	TASK_ESTIMATED_DURATION: 'http://alopexcare.com/fhir/StructureDefinition/task-estimated-duration',

	/** Task call center metadata (outcomeType, attemptNumber, outreachMethod, etc.) */
	TASK_CALL_METADATA: 'http://alopexcare.com/fhir/StructureDefinition/task-call-metadata',

	/** Enrollment status on Patient */
	ENROLLMENT_STATUS: 'http://alopexcare.com/fhir/StructureDefinition/enrollment-status',

	/** Check severity for eligibility validation */
	CHECK_SEVERITY: 'http://alopexcare.com/fhir/StructureDefinition/check-severity',

	/** Check blocking flag for eligibility validation */
	CHECK_BLOCKING: 'http://alopexcare.com/fhir/StructureDefinition/check-blocking',

	/** Qualified ICD codes from eligibility check */
	QUALIFIED_ICD_CODES: 'http://alopexcare.com/fhir/StructureDefinition/qualified-icd-codes',

	/** Unqualified ICD codes from eligibility check */
	UNQUALIFIED_ICD_CODES: 'http://alopexcare.com/fhir/StructureDefinition/unqualified-icd-codes',

	/** Validation version for eligibility rules */
	VALIDATION_VERSION: 'http://alopexcare.com/fhir/StructureDefinition/validation-version',

	/** Qualification result reference */
	QUALIFICATION_RESULT: 'http://alopexcare.com/fhir/StructureDefinition/qualification-result',

	/** Review metadata */
	REVIEW_METADATA: 'http://alopexcare.com/fhir/StructureDefinition/review-metadata',

	/** Program ID extension */
	PROGRAM_ID: 'http://alopexcare.com/fhir/StructureDefinition/program-id',

	/** Session status for time tracking */
	SESSION_STATUS: 'http://alopexcare.com/fhir/StructureDefinition/session-status',

	/** Last heartbeat for session tracking */
	LAST_HEARTBEAT: 'http://alopexcare.com/fhir/StructureDefinition/last-heartbeat',

	/** Tracking subcategory for session tracking */
	TRACKING_SUBCATEGORY: 'http://alopexcare.com/fhir/StructureDefinition/tracking-subcategory',

	/** Contact notes on RelatedPerson */
	CONTACT_NOTES: 'http://alopexcare.com/fhir/StructureDefinition/contact-notes',

	/** Proxy relationship on consent performer */
	PROXY_RELATIONSHIP: 'http://alopexcare.com/fhir/StructureDefinition/proxy-relationship',

	/** Contact telecom extension */
	CONTACT_TELECOM: 'http://alopexcare.com/fhir/StructureDefinition/contact-telecom',

	/** Enabled programs on Organization */
	ENABLED_PROGRAMS: 'http://alopexcare.com/fhir/StructureDefinition/enabled-programs',

	/** Program code extension */
	PROGRAM_CODE: 'http://alopexcare.com/program-code',

	/** Status reason on EpisodeOfCare (why it's in current status) */
	STATUS_REASON: 'http://alopexcare.com/fhir/StructureDefinition/status-reason',

	/** Legacy referral ID for backward compatibility */
	LEGACY_REFERRAL_ID: 'http://alopexcare.com/legacy-referral-id',

	/** Last activity date on ServiceRequest (stamped on every referral save) */
	LAST_ACTIVITY_DATE: 'http://alopexcare.com/fhir/StructureDefinition/last-activity-date',

	/** Whether an AccessPolicy requires NPI for assigned users */
	REQUIRES_NPI: 'http://alopexcare.com/fhir/Extension/requires-npi',

	/** Patient communication preference (phone, email, text, mail) */
	COMMUNICATION_PREFERENCE:
		'http://alopexcare.com/fhir/StructureDefinition/communication-preference',

	/** Patient preferred call times */
	PREFERRED_CALL_TIMES: 'http://alopexcare.com/fhir/StructureDefinition/preferred-call-times',
} as const;

/**
 * Organization Type Codes
 * Codes for Organization.type to identify organization category
 * Reference: http://terminology.hl7.org/CodeSystem/organization-type
 */
export const ORGANIZATION_TYPE_CODES = {
	/** Pharmacy */
	PHARMACY: 'prov',

	/** Healthcare Provider */
	HEALTHCARE_PROVIDER: 'prov',
} as const;

/**
 * Organization Type System URL
 * Standard HL7 CodeSystem for organization types
 */
export const ORGANIZATION_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/organization-type';

/**
 * Custom Organization Identifier Systems
 * Custom systems for organization-specific identifiers
 */
export const CUSTOM_IDENTIFIER_SYSTEMS = {
	/** Organization's internal member ID */
	MEMBER_ID: 'http://alopexcare.com/member-id',

	/** Organization's internal medical record number */
	MRN: 'http://alopexcare.com/mrn',

	/** Organization's pharmacy identifier */
	PHARMACY_ID: 'http://alopexcare.com/pharmacy-id',

	/** Legacy user ID for backward compatibility */
	USER_ID: 'http://alopexcare.com/user-id',

	/** Legacy entity ID for organization references */
	ENTITY_ID: 'http://alopexcare.com/entity-id',

	/** Provider ID (legacy system identifier for backward compatibility) */
	PROVIDER_ID: 'http://alopexcare.com/provider-id',

	/** Bot identifier system */
	BOT_IDENTIFIER: 'http://alopexcare.com/fhir/Bot',

	/** Entity root marker for the root Organization in the entity hierarchy */
	ENTITY_ROOT: 'http://alopexcare.com/entity-root',
} as const;

/**
 * Custom CodeSystems
 * Organization-specific code systems for custom data
 */
export const CUSTOM_CODE_SYSTEMS = {
	/** Service request categories (enrollment, etc.) */
	SERVICE_CATEGORY: 'http://alopexcare.com/service-category',

	/** Care programs (CCM, RPM, RTM, BHI) */
	PROGRAMS: 'http://alopexcare.com/fhir/CodeSystem/program-types',

	/** Referral status codes */
	REFERRAL_STATUS: 'http://alopexcare.com/referral-status',

	/** Episode of care type (program enrollment type) */
	EPISODE_TYPE: 'http://alopexcare.com/fhir/CodeSystem/episode-type',

	/** User roles (admin, care-coordinator, provider, etc.) */
	USER_ROLES: 'http://alopexcare.com/fhir/CodeSystem/user-roles',

	/** Medical group affiliation types */
	AFFILIATION_TYPES: 'http://alopexcare.com/affiliation-types',

	/** Task types (ELIGIBILITY_REVIEW, CONSENT_CALL, etc.) */
	TASK_TYPES: 'http://alopexcare.com/fhir/CodeSystem/task-types',

	/** Call outcome types for task call center metadata */
	CALL_OUTCOME_TYPES: 'http://alopexcare.com/fhir/CodeSystem/call-outcome-types',

	/** Outreach methods (phone, email, in_person) */
	OUTREACH_METHODS: 'http://alopexcare.com/fhir/CodeSystem/outreach-methods',

	/** Languages */
	LANGUAGES: 'http://alopexcare.com/fhir/CodeSystem/languages',

	/** Religions */
	RELIGIONS: 'http://alopexcare.com/fhir/CodeSystem/religions',

	/** Eligibility status codes */
	ELIGIBILITY_STATUS: 'http://alopexcare.com/fhir/CodeSystem/eligibility-status-codes',

	/** Check severity codes */
	CHECK_SEVERITY: 'http://alopexcare.com/fhir/CodeSystem/check-severity-codes',

	/** Observation types */
	OBSERVATION_TYPES: 'http://alopexcare.com/CodeSystem/observation-types',

	/** Eligibility types */
	ELIGIBILITY_TYPES: 'http://alopexcare.com/fhir/CodeSystem/eligibility-types',

	/** Relationships */
	RELATIONSHIPS: 'http://alopexcare.com/fhir/CodeSystem/relationships',

	/** Session types */
	SESSION_TYPES: 'http://alopexcare.com/CodeSystem/session-types',

	/** Consent types */
	CONSENT_TYPES: 'http://alopexcare.com/CodeSystem/consent-types',

	/** Consent parties */
	CONSENT_PARTIES: 'http://alopexcare.com/CodeSystem/consent-parties',

	/** Consent method */
	CONSENT_METHOD: 'http://alopexcare.com/CodeSystem/consent-method',

	/** Requested info categories */
	REQUESTED_INFO: 'http://alopexcare.com/CodeSystem/requested-info',

	/** Entity types for organizations */
	ENTITY_TYPES: 'http://alopexcare.com/fhir/CodeSystem/entity-types',

	/** Vital measurement methods */
	VITAL_METHODS: 'http://alopexcare.com/fhir/CodeSystem/vital-methods',

	/** Condition status codes */
	CONDITION_STATUS: 'http://alopexcare.com/CodeSystem/condition-status',

	/** Tracking categories */
	TRACKING_CATEGORIES: 'http://alopexcare.com/fhir/CodeSystem/tracking-categories',

	/** Tracking subcategories */
	TRACKING_SUBCATEGORIES: 'http://alopexcare.com/fhir/CodeSystem/tracking-subcategories',

	/** Flag source codes */
	FLAG_SOURCE: 'http://alopexcare.com/fhir/CodeSystem/flag-source-codes',

	/** App permissions */
	APP_PERMISSIONS: 'http://alopexcare.com/fhir/CodeSystem/app-permissions',

	/** Care team roles */
	CARE_TEAM_ROLES: 'http://alopexcare.com/fhir/CodeSystem/care-team-roles',

	/** Eligibility status (simplified) */
	ELIGIBILITY_STATUS_SIMPLE: 'http://alopexcare.com/CodeSystem/eligibility-status',

	/** Episode of care status reasons (enrollment pipeline cancellation/hold reasons) */
	EPISODE_STATUS_REASON: 'http://alopexcare.com/fhir/CodeSystem/episode-status-reason',

	/** Communication preference codes (phone, email, text, mail) */
	COMMUNICATION_PREFERENCE: 'http://alopexcare.com/fhir/CodeSystem/communication-preference',
} as const;

/**
 * Identifier Type Codes
 * Common codes from http://terminology.hl7.org/CodeSystem/v2-0203
 */
export const IDENTIFIER_TYPE_CODES = {
	/** Medical Record Number */
	MR: 'MR',

	/** Social Security Number */
	SS: 'SS',

	/** Driver's License Number */
	DL: 'DL',

	/** Member Number */
	MB: 'MB',

	/** Patient Internal Identifier */
	PI: 'PI',

	/** National Provider Identifier */
	NPI: 'NPI',
} as const;

/**
 * Marital Status Codes
 * Common codes from http://terminology.hl7.org/CodeSystem/v3-MaritalStatus
 */
export const MARITAL_STATUS_CODES = {
	/** Annulled */
	ANNULLED: 'A',

	/** Divorced */
	DIVORCED: 'D',

	/** Interlocutory */
	INTERLOCUTORY: 'I',

	/** Legally Separated */
	LEGALLY_SEPARATED: 'L',

	/** Married */
	MARRIED: 'M',

	/** Polygamous */
	POLYGAMOUS: 'P',

	/** Never Married / Single */
	NEVER_MARRIED: 'S',

	/** Domestic Partner */
	DOMESTIC_PARTNER: 'T',

	/** Unmarried */
	UNMARRIED: 'U',

	/** Widowed */
	WIDOWED: 'W',

	/** Unknown */
	UNKNOWN: 'UNK',
} as const;

/**
 * LOINC Vital Signs Codes
 * Standard LOINC codes for common vital signs observations
 * Reference: https://loinc.org/
 */
export const LOINC_VITAL_SIGNS = {
	/** Blood Pressure Panel - 85354-9 */
	BLOOD_PRESSURE_PANEL: '85354-9',

	/** Systolic Blood Pressure - 8480-6 */
	BLOOD_PRESSURE_SYSTOLIC: '8480-6',

	/** Diastolic Blood Pressure - 8462-4 */
	BLOOD_PRESSURE_DIASTOLIC: '8462-4',

	/** Heart Rate - 8867-4 */
	HEART_RATE: '8867-4',

	/** Body Temperature - 8310-5 */
	BODY_TEMPERATURE: '8310-5',

	/** Respiratory Rate - 9279-1 */
	RESPIRATORY_RATE: '9279-1',

	/** Oxygen Saturation - 2708-6 */
	OXYGEN_SATURATION: '2708-6',

	/** Body Weight - 29463-7 */
	BODY_WEIGHT: '29463-7',

	/** Body Height - 8302-2 */
	BODY_HEIGHT: '8302-2',

	/** Body Mass Index (BMI) - 39156-5 */
	BMI: '39156-5',

	/** Blood Glucose - 2339-0 */
	BLOOD_GLUCOSE: '2339-0',

	/** Head Circumference - 9843-4 */
	HEAD_CIRCUMFERENCE: '9843-4',
} as const;

/**
 * Vital Sign Units (UCUM - Unified Code for Units of Measure)
 */
export const VITAL_SIGN_UNITS = {
	/** Millimeters of Mercury (for blood pressure) */
	MM_HG: 'mm[Hg]',

	/** Beats per minute (for heart rate) */
	BPM: '/min',

	/** Degrees Fahrenheit */
	DEGREES_F: '[degF]',

	/** Degrees Celsius */
	DEGREES_C: 'Cel',

	/** Percentage (for oxygen saturation) */
	PERCENT: '%',

	/** Kilograms */
	KILOGRAMS: 'kg',

	/** Pounds */
	POUNDS: '[lb_av]',

	/** Centimeters */
	CENTIMETERS: 'cm',

	/** Inches */
	INCHES: '[in_i]',

	/** Milligrams per deciliter (for blood glucose) */
	MG_DL: 'mg/dL',

	/** Kilograms per square meter (for BMI) */
	KG_M2: 'kg/m2',

	/** Breaths per minute */
	BREATHS_PER_MIN: '/min',
} as const;

/**
 * Helper function to get identifier type coding
 */
export function getIdentifierTypeCoding(code: keyof typeof IDENTIFIER_TYPE_CODES) {
	return {
		system: HL7_CODE_SYSTEMS.IDENTIFIER_TYPE,
		code: IDENTIFIER_TYPE_CODES[code],
	};
}

/**
 * Helper function to get marital status coding
 */
export function getMaritalStatusCoding(code: keyof typeof MARITAL_STATUS_CODES) {
	return {
		system: HL7_CODE_SYSTEMS.MARITAL_STATUS,
		code: MARITAL_STATUS_CODES[code],
	};
}

// ============================================================================
// TASK STATUS AND PRIORITY MAPPINGS
// ============================================================================

/**
 * Legacy Task Status to FHIR Task.status mapping
 * Maps internal task statuses to standard FHIR Task status codes
 */
export const TASK_STATUS_TO_FHIR = {
	pending: 'requested',
	assigned: 'accepted',
	in_progress: 'in-progress',
	completed: 'completed',
	cancelled: 'cancelled',
	deferred: 'on-hold',
} as const;

/**
 * FHIR Task.status to Legacy Task Status mapping (reverse)
 */
export const FHIR_TO_TASK_STATUS = {
	requested: 'pending',
	received: 'pending',
	accepted: 'assigned',
	rejected: 'cancelled',
	ready: 'assigned',
	'in-progress': 'in_progress',
	'on-hold': 'deferred',
	completed: 'completed',
	cancelled: 'cancelled',
	'entered-in-error': 'cancelled',
	failed: 'cancelled',
	draft: 'pending',
} as const;

/**
 * Task Status type for legacy system
 */
export type LegacyTaskStatus = keyof typeof TASK_STATUS_TO_FHIR;

/**
 * FHIR Task Status type
 */
export type FhirTaskStatus = (typeof TASK_STATUS_TO_FHIR)[LegacyTaskStatus];

/**
 * Maps legacy priority level (0-10) to FHIR Task.priority
 */
export function mapPriorityToFhir(priorityLevel: number | undefined) {
	if (priorityLevel === undefined || priorityLevel === null) return 'routine';
	if (priorityLevel <= 2) return 'routine';
	if (priorityLevel <= 5) return 'urgent';
	if (priorityLevel <= 8) return 'asap';
	return 'stat';
}

/**
 * Maps FHIR Task.priority to legacy priority level (0-10)
 */
export function mapFhirToPriority(fhirPriority: string | undefined) {
	switch (fhirPriority) {
		case 'stat':
			return 10;
		case 'asap':
			return 7;
		case 'urgent':
			return 4;
		case 'routine':
		default:
			return 1;
	}
}

/**
 * Task type codes from our custom CodeSystem
 */
export const TASK_TYPE_CODES = {
	ELIGIBILITY_REVIEW: 'ELIGIBILITY_REVIEW',
	CONSENT_CALL: 'CONSENT_CALL',
	OUTREACH_CALL: 'OUTREACH_CALL',
	FOLLOW_UP_CALL: 'FOLLOW_UP_CALL',
	DOCUMENT_REVIEW: 'DOCUMENT_REVIEW',
	ENROLLMENT_COMPLETION: 'ENROLLMENT_COMPLETION',
	CARE_COORDINATION: 'CARE_COORDINATION',
	ASSESSMENT: 'ASSESSMENT',
	CARE_PLAN_REVIEW: 'CARE_PLAN_REVIEW',
	GENERAL: 'GENERAL',
	/** Time entry for tracking work duration */
	TIME_ENTRY: 'TIME_ENTRY',
} as const;

/**
 * Helper to create task type CodeableConcept
 */
export function getTaskTypeCoding(code: keyof typeof TASK_TYPE_CODES) {
	return {
		system: CUSTOM_CODE_SYSTEMS.TASK_TYPES,
		code: TASK_TYPE_CODES[code],
	};
}
